import {
	faBuildingColumns,
	faCreditCard,
	faDownload,
	faGift,
	faHistory,
	faHome,
	faLanguage,
	faMoneyBillTransfer,
	faMessage,
	faPaperPlane,
	faPercent,
	faShieldHalved,
	faTag,
	faExclamationCircle,
	faUser,
} from "@fortawesome/free-solid-svg-icons";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import QRCode from "qrcode";
import Swal from "sweetalert2";
import { AppContext } from "./AppContext";
import { logoutAction } from "./actions/auth";
import { isNullOrUndefined, processNumValue } from "./utils";
import { modeTypes } from "./constants/mode";
import {
	seamlessChangePassword,
	seamlessGetUserBalance,
} from "./services/seamless.service";
import {
	hybridChangePassword,
	hybridGetUserBalance,
	parseHybridUserBalance,
} from "./services/hybridPlayer.service";
import {
	bindPasswordHint,
	buildPasswordHintHtml,
	isPasswordValid,
} from "./utils/password";

export const useMenu = () => {
	const { t, i18n } = useTranslation();
	const { userProfile } = useSelector((state: any) => state.userProfile);
	const [balance, setBalance] = useState(0);
	const { setModalState } = useContext(AppContext);
	const dispatch = useDispatch();
	const currentMode = localStorage.getItem("currentMode") || modeTypes.TRANSFER;

	const copyToClipboard = async (text: string) => {
		// navigator.clipboard requires secure context (https) and user gesture.
		if (navigator?.clipboard?.writeText && window.isSecureContext) {
			await navigator.clipboard.writeText(text);
			return;
		}

		const el = document.createElement("textarea");
		el.value = text;
		el.setAttribute("readonly", "");
		el.style.position = "fixed";
		el.style.left = "-9999px";
		el.style.top = "0";
		document.body.appendChild(el);
		el.select();

		const ok = document.execCommand("copy");
		document.body.removeChild(el);

		if (!ok) throw new Error("Copy failed");
	};

	const handleSupportModal = () => {
		setModalState("support");
	};

	const handleMama4d = () => {
		// Open mama4d.app in a new tab when the page loads
		window.open("https://mama4d.app", "_blank", "noopener,noreferrer");
	};

	useEffect(() => {
		if (isNullOrUndefined(userProfile)) {
			return;
		}
		if (currentMode === modeTypes.SEAMLESS) {
			seamlessGetUserBalance().then((result) => {
				if (result.status) {
					setBalance(result.data);
				}
			});
		} else if (currentMode === modeTypes.HYBRID) {
			hybridGetUserBalance().then((result) => {
				if (result.status) {
					const { balance: nextBalance } = parseHybridUserBalance(result.data);
					setBalance(nextBalance);
				}
			});
		}
	}, [userProfile, currentMode]);

	const handleLogout = () => {
		Swal.fire({
			icon: "warning",
			text: t("Are you sure you want to logout?"),
			showCancelButton: true,
			confirmButtonColor: "#1e9fd9",
			cancelButtonColor: "grey",
			confirmButtonText: t("Confirm"),
			cancelButtonText: t("Cancel"),
			allowOutsideClick: false,
		}).then((result) => {
			if (result.isConfirmed) {
				dispatch(logoutAction());
			}
			return true;
		});
	};

	const handleShowReferralCode = async () => {
		if (isNullOrUndefined(userProfile)) return;

		const referralCode =
			userProfile?.[0]?.hybrid_player_username || "Not available";
		const hasReferralCode = referralCode !== "Not available";

		// Build a deep-link URL that opens the register form directly
		const baseUrl = window.location.origin;
		const registerUrl = hasReferralCode
			? `${baseUrl}/?action=register&ref=${encodeURIComponent(referralCode)}`
			: `${baseUrl}/?action=register`;

		const result = await Swal.fire({
			title: `<span style="color:#1e9fd9">${t("Your Referral Code")}</span>`,
			html: `
				<div style="display: flex; flex-direction: column; align-items: center; padding: 10px 0;">
					<div id="qr-container" style="background: white; padding: 16px; border-radius: 12px; margin-bottom: 20px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">
						<!-- QR Code will be rendered here -->
					</div>
					
					<div style="display: flex; align-items: center; gap: 10px; font-size: 24px; font-weight: 700; color: #1e9fd9; letter-spacing: 4px; margin-bottom: 12px; font-family: monospace;">
						<span>${referralCode}</span>
						${
							hasReferralCode
								? `
							<span
								id="copy-referral-code-icon"
								title="${t("Copy Code")}"
								style="cursor: pointer; font-size: 13px; letter-spacing: normal;"
							>📋</span>
						`
								: ""
						}
					</div>
					
					<div style="color: #666; font-size: 14px; max-width: 280px; text-align: center; line-height: 1.5;">
						Scan this QR code to open the registration page with your referral code pre-filled.
					</div>
				</div>
			`,
			icon: "info",
			confirmButtonText: "📋 Copy Link",
			showCancelButton: true,
			cancelButtonText: t("Close"),
			width: 420,
			customClass: {
				popup: "referral-modal",
				confirmButton: "btn btn-primary btn-lg",
				cancelButton: "btn btn-secondary btn-lg",
			},
			buttonsStyling: false,
			didOpen: () => {
				const container = document.getElementById("qr-container");
				if (container) {
					QRCode.toDataURL(registerUrl, {
						width: 180,
						margin: 1,
						color: {
							dark: "#1e9fd9",
							light: "#FFFFFF",
						},
					})
						.then((url) => {
							const img = document.createElement("img");
							img.src = url;
							img.style.width = "180px";
							img.style.height = "180px";
							container.appendChild(img);
						})
						.catch((err) => {
							console.error("QR Code generation failed:", err);
							container.innerHTML = `<div style="color: red; padding: 20px;">Failed to generate QR code</div>`;
						});
				}

				if (hasReferralCode) {
					const copyIcon = document.getElementById("copy-referral-code-icon");
					copyIcon?.addEventListener("click", async () => {
						try {
							await copyToClipboard(referralCode);
							Swal.fire({
								icon: "success",
								title: "✅ Copied!",
								text: t("Referral code has been copied to clipboard"),
								timer: 1800,
								showConfirmButton: false,
								toast: true,
								position: "top-end",
							});
						} catch (err) {
							Swal.fire({
								icon: "error",
								title: "Failed to copy",
								text: t("Please copy manually"),
							});
						}
					});
				}
			},
		});

		if (result.isConfirmed) {
			try {
				await copyToClipboard(registerUrl);
				Swal.fire({
					icon: "success",
					title: "✅ Copied!",
					text: t("Registration link has been copied to clipboard"),
					timer: 1800,
					showConfirmButton: false,
					toast: true,
					position: "top-end",
				});
			} catch (err) {
				Swal.fire({
					icon: "error",
					title: "Failed to copy",
					text: t("Please copy manually"),
				});
			}
		}
	};
	const isHybrid = currentMode === modeTypes.HYBRID;
	const handleChangePassword = async () => {
		if (isNullOrUndefined(userProfile)) return; // prevent crash if no userProfile

		const swalClass = {
			popup:
				currentMode === modeTypes.SEAMLESS
					? "custom-swal-sm seamless-mode"
					: "custom-swal-sm transfer-mode",
			title: "custom-swal-title",
			htmlContainer: "custom-swal-html",
			confirmButton:
				currentMode === modeTypes.SEAMLESS
					? "custom-swal-confirm-btn seamless-mode"
					: "custom-swal-confirm-btn transfer-mode",
			cancelButton: "custom-swal-cancel-btn",
			closeButton: "swal-close-btn",
		};

		const showAlert = (options: any) =>
			Swal.fire({ customClass: swalClass, ...options });
		const showLoading = (title: string) =>
			showAlert({
				title,
				allowOutsideClick: false,
				didOpen: () => Swal.showLoading(),
			});

		const username =
			currentMode === modeTypes.HYBRID
				? userProfile?.[0]?.hybrid_player_username ||
					userProfile?.[0]?.player_username ||
					"-"
				: userProfile?.[0]?.player_username || "-";
		const displayName =
			currentMode === modeTypes.HYBRID
				? userProfile?.[0]?.hybrid_player_name || "-"
				: userProfile?.[0]?.player_nickname || "-";
		const phoneNo = userProfile?.[0]?.player_phone_no || "-";

		try {
			const { value: formValues } = await showAlert({
				width: 340,
				title: `
					<div class="custom-swal-header">
						<span>${t("Account")}</span>
					</div>
				`,
				html: `
					<div class="account-info-box">
						<div class="account-info-row">
							<label class="swal-label">${t("Name")}</label>
							<div class="swal-display-field">${displayName}</div>
						</div>
						<div class="account-info-row">
							<label class="swal-label">${t("Username")}</label>
							<div class="swal-display-field">${username}</div>
						</div>
						<div class="account-info-row">
							<label class="swal-label">${t("Score")}</label>
							<div class="swal-display-field">${processNumValue(balance, 2)}</div>
						</div>
						<div class="account-info-row">
							<label class="swal-label">${t("Phone Number")}</label>
							<div class="swal-display-field">${phoneNo}</div>
						</div>
					</div>

					<div class="swal-section-divider"></div>

					 <div class="reset-password-box">
						<div class="reset-title">${t("Reset Password")}</div>

                        <div class="reset-password-row password-toggle-row">
                            <label class="swal-label">${t("Current Password")}</label>
                            <div class="swal-password-col">
                                <div class="swal-password-wrapper">
                                    <input type="password" id="oldPassword" class="swal-input-field"
                                        placeholder="${t("Enter current password")}" autocomplete="current-password" />
                                    <span class="toggle-password-btn" data-target="oldPassword">
                                        <i class="fa fa-eye-slash"></i>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="reset-password-row password-toggle-row">
                            <label class="swal-label">${t("New Password")}</label>
                            <div class="swal-password-col">
                                <div class="swal-password-wrapper">
                                    <input type="password" id="newPassword" class="swal-input-field"
                                        placeholder="${t("Enter new password")}" autocomplete="new-password" />
                                    <span class="toggle-password-btn" data-target="newPassword">
                                        <i class="fa fa-eye-slash"></i>
                                    </span>
                                </div>
                                ${isHybrid ? buildPasswordHintHtml(t, i18n.language, "newPasswordHint") : ""}
                            </div>
                        </div>
					</div>
				`,
				focusConfirm: false,
				showCancelButton: true,
				confirmButtonText: t("Submit"),
				cancelButtonText: t("Cancel"),
				showCloseButton: true,
				closeButtonHtml: "×",
				didOpen: (popup: HTMLElement) => {
					document.querySelectorAll(".toggle-password-btn").forEach((btn) => {
						btn.addEventListener("click", () => {
							const targetId = btn.getAttribute("data-target");
							const input = document.getElementById(
								targetId
							) as HTMLInputElement;
							const icon = btn.querySelector("i");

							if (input.type === "password") {
								input.type = "text";
								icon.classList.remove("fa-eye-slash");
								icon.classList.add("fa-eye");
							} else {
								input.type = "password";
								icon.classList.remove("fa-eye");
								icon.classList.add("fa-eye-slash");
							}
						});
					});

					if (isHybrid) {
						const input = popup.querySelector<HTMLInputElement>("#newPassword");
						const hint = popup.querySelector<HTMLElement>("#newPasswordHint");
						if (input && hint) bindPasswordHint(input, hint);
					}
				},
				preConfirm: () => {
					const oldPassword = (
						document.getElementById("oldPassword") as HTMLInputElement
					)?.value.trim();
					const newPassword = (
						document.getElementById("newPassword") as HTMLInputElement
					)?.value.trim();

					if (!oldPassword || !newPassword) {
						Swal.showValidationMessage(
							t("Please enter both old and new passwords")
						);
						return false;
					}
					return { oldPassword, newPassword };
				},
			});

			if (!formValues) return;
			showLoading(t("Processing..."));

			const { oldPassword, newPassword } = formValues;
			const response =
				currentMode === modeTypes.HYBRID
					? await hybridChangePassword({
							old_password: oldPassword,
							new_password: newPassword,
						})
					: await seamlessChangePassword(oldPassword, newPassword);
			Swal.close();

			if (response.status) {
				await showAlert({
					icon: "success",
					title: undefined,
					width: 300,
					text: t("Password Changed Successfully"),
				});
			} else {
				const reason = response?.message ? t(response.message) : "";
				await showAlert({
					icon: "error",
					title: undefined,
					width: 300,
					text: reason
						? `${t("Failed to change password")}: ${reason}`
						: t("Failed to change password"),
				});
			}
		} catch (error) {
			console.error(error);
			Swal.close();
			await showAlert({
				icon: "error",
				title: undefined,
				text: t("Unable to change password. Please try again later."),
			});
		}
	};

	const menu = [
		{
			id: "home",
			label: "Home",
			value:
				currentMode === modeTypes.HYBRID
					? "/"
					: isNullOrUndefined(userProfile) || currentMode == modeTypes.SEAMLESS
						? "/home"
						: "/lobby",
			icon: faHome,
			type: "link",
			access: false,
			mobile: true,
			offCanvas: false,
			iconType: "image",
		},
		{
			id: "promotion",
			label: "Promotion",
			value: "/promotion",
			icon: faHome,
			type: "link",
			access: true,
			mobile: true,
			offCanvas: true,
			iconType: "image",
		},
		{
			id: "download",
			label: "Download",
			value: "/download",
			icon: faDownload,
			type: "link",
			access: true,
			mobile: true,
			offCanvas: true,
			iconType: "image",
		},
		{
			id: "account",
			label: "Account",
			value: !isNullOrUndefined(userProfile) ? handleChangePassword : null,
			icon: faUser,
			type: "action",
			access:
				!isNullOrUndefined(userProfile) &&
				(currentMode == modeTypes.SEAMLESS || currentMode == modeTypes.HYBRID),
			mobile: true,
			offCanvas:
				!isNullOrUndefined(userProfile) &&
				(currentMode == modeTypes.SEAMLESS || currentMode == modeTypes.HYBRID),
			iconType: "fontAwesome",
		},
		{
			id: "bank_account",
			label: "Bank Account",
			value: "/bank-account",
			icon: faCreditCard,
			type: "link",
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			mobile: false,
			offCanvas:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			iconType: "fontAwesome",
		},
		{
			id: "security",
			label: "Security",
			value: "/security",
			icon: faShieldHalved,
			type: "link",
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			mobile: false,
			offCanvas:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			iconType: "fontAwesome",
		},
		{
			id: "deposit",
			label: "Deposit",
			value: "/deposit",
			icon: faBuildingColumns,
			type: "link",
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			mobile: true,
			offCanvas:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			iconType: "fontAwesome",
		},
		{
			id: "bonus",
			label: "Bonus",
			value: "/bonus",
			icon: faTag,
			type: "link",
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			mobile: true,
			offCanvas:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			iconType: "fontAwesome",
		},
		{
			id: "withdraw",
			label: "Withdraw",
			value: "/withdraw",
			icon: faMoneyBillTransfer,
			type: "link",
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			mobile: true,
			offCanvas:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			iconType: "fontAwesome",
		},
		{
			id: "send",
			label: "Send",
			value: "/send",
			icon: faPaperPlane,
			type: "link",
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			mobile: false,
			offCanvas:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			iconType: "fontAwesome",
		},
		{
			id: "rebate",
			label: "Rebate",
			value: "/rebate",
			icon: faPercent,
			type: "link",
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			mobile: false,
			offCanvas:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			iconType: "fontAwesome",
		},
		{
			id: "claims",
			label: "Claims",
			value: "/claims",
			icon: faGift,
			type: "link",
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			mobile: false,
			offCanvas:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			iconType: "fontAwesome",
		},
		{
			id: "history",
			label: "History",
			value: "/history",
			icon: faHistory,
			type: "modalhistory",
			access: true,
			mobile: true,
			offCanvas: true,
			iconType: "fontAwesome",
		},
		{
			id: "report_problem",
			label: "Report a Problem",
			value: "/report-problem",
			icon: faExclamationCircle,
			type: "link",
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			mobile: false,
			offCanvas:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			iconType: "fontAwesome",
		},
		{
			id: "chat",
			label: "Chat",
			value: "/chat",
			icon: faMessage,
			type: "link",
			access: true,
			mobile: true,
			offCanvas: true,
			iconType: "fontAwesome",
		},
		{
			id: "referral_code",
			label: "Referral Code",
			value: !isNullOrUndefined(userProfile) ? handleShowReferralCode : null,
			icon: faTag,
			type: "action",
			access:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			mobile: true,
			offCanvas:
				!isNullOrUndefined(userProfile) && currentMode === modeTypes.HYBRID,
			iconType: "fontAwesome",
		},
		// {
		// 	id: "support",
		// 	label: "24/7 Support",
		// 	value: handleSupportModal,
		// 	icon: faHeadset,
		// 	type: "action",
		// 	access: true,
		// 	mobile: true,
		// 	offCanvas: false,
		// },
		// {
		// 	id: "mama4d",
		// 	label: "Mama4D",
		// 	value: handleMama4d,
		// 	icon: faHeadset,
		// 	type: "action",
		// 	access: true,
		// 	mobile: false,
		// 	offCanvas: true,
		// },
		{
			id: "language",
			label: "Language",
			value: null,
			icon: faLanguage,
			type: "dropdown",
			access: true,
			mobile: true,
			offCanvas: true,
			iconType: "image",
		},
		// {
		// 	id: "logout",
		// 	label: "Logout",
		// 	value: handleLogout,
		// 	icon: faSignOut,
		// 	type: "action",
		// 	access: !isNullOrUndefined(userProfile),
		// 	mobile: false,
		// 	offCanvas: !isNullOrUndefined(userProfile),
		// },
	];

	return menu.filter(
		(item) =>
			!(
				currentMode === modeTypes.TRANSFER &&
				(item.id === "account" || item.id === "history")
			)
	);
};
